import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const { user, token } = useAuth();

  useEffect(() => {
    // Create socket connection with correct server URL (connect always, guest or authenticated)
    const serverUrl = process.env.REACT_APP_SERVER_URL || '';
    const newSocket = io(serverUrl, {
      auth: token ? { token } : {},
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setIsConnected(true);

      // Join rooms if user is authenticated
      if (user) {
        // Join raw user ID room
        newSocket.emit('join-room', String(user.id));
        // Join user-specific prefixed room
        newSocket.emit('join-room', `user-${user.id}`);

        // Join role-based room
        if (user.role === 'admin' || user.role === 'sub-admin') {
          newSocket.emit('join-room', 'admin-room');
        }
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnected(false);
    });

    // Reconnection events
    newSocket.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);

      // Rejoin rooms after reconnection
      if (user) {
        newSocket.emit('join-room', String(user.id));
        newSocket.emit('join-room', `user-${user.id}`);
        if (user.role === 'admin' || user.role === 'sub-admin') {
          newSocket.emit('join-room', 'admin-room');
        }
      }
    });

    newSocket.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error);
    });

    // Error handling
    newSocket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    setSocket(newSocket);

    // Cleanup function
    return () => {
      console.log('Cleaning up socket connection');
      newSocket.close();
    };
  }, [token, user]);

  // Emit events
  const emitEvent = (eventName, data) => {
    if (socket && isConnected) {
      socket.emit(eventName, data);
      return true;
    }
    return false;
  };

  // Join room
  const joinRoom = (roomName) => {
    if (socket && isConnected) {
      socket.emit('join-room', roomName);
      return true;
    }
    return false;
  };

  // Leave room
  const leaveRoom = (roomName) => {
    if (socket && isConnected) {
      socket.emit('leave-room', roomName);
      return true;
    }
    return false;
  };

  // Listen to specific events
  const onEvent = (eventName, callback) => {
    if (socket) {
      socket.on(eventName, callback);

      // Return cleanup function
      return () => {
        socket.off(eventName, callback);
      };
    }
  };

  // Remove event listener
  const offEvent = (eventName, callback) => {
    if (socket) {
      socket.off(eventName, callback);
    }
  };

  const value = {
    socket,
    isConnected,
    emitEvent,
    joinRoom,
    leaveRoom,
    onEvent,
    offEvent
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
